import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import documentService from '../services/documentService';

const DocumentLibrary = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMetadata, setUploadMetadata] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
  });
  
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  const [filters, setFilters] = useState({
    search: '',
    document_type: '',
    category: '',
    source: '',
    include_archived: false,
  });

  useEffect(() => {
    loadDocuments();
  }, [projectId, filters]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentService.listDocuments({
        project_id: projectId,
        ...filters,
      });
      setDocuments(response.documents || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadMetadata.title) {
        setUploadMetadata({ ...uploadMetadata, title: file.name });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const metadata = {
        ...uploadMetadata,
        project_id: projectId,
        tags: uploadMetadata.tags ? uploadMetadata.tags.split(',').map(t => t.trim()) : [],
      };

      await documentService.uploadDocument(selectedFile, metadata);
      setSuccess('Document uploaded successfully');
      setUploadOpen(false);
      setSelectedFile(null);
      setUploadMetadata({ title: '', description: '', category: '', tags: '' });
      loadDocuments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (doc) => {
    try {
      const fullDoc = await documentService.getDocument(doc.id);
      setSelectedDocument(fullDoc);
      setViewOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      await documentService.downloadDocument(doc.id, doc.file_name);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to download document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setLoading(true);
      await documentService.deleteDocument(docId);
      setSuccess('Document deleted successfully');
      loadDocuments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveToggle = async (doc) => {
    try {
      setLoading(true);
      await documentService.updateDocument(doc.id, {
        is_archived: !doc.is_archived,
      });
      setSuccess(doc.is_archived ? 'Document restored' : 'Document archived');
      loadDocuments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    const icons = {
      MARKDOWN: '📝',
      PDF: '📄',
      DOCX: '📃',
      TXT: '📄',
      IMAGE: '🖼️',
      VIDEO: '🎥',
      SPREADSHEET: '📊',
      PRESENTATION: '📊',
      OTHER: '📎',
    };
    return icons[type] || '📎';
  };

  const getSourceColor = (source) => {
    const colors = {
      USER_UPLOAD: { bg: '#F3F4F6', text: '#374151' },
      AI_GENERATED: { bg: '#DBEAFE', text: '#1E40AF' },
      AI_ACTION: { bg: '#E9D5FF', text: '#6B21A8' },
      WORKFLOW: { bg: '#CFFAFE', text: '#0E7490' },
      INTEGRATION: { bg: '#FEF3C7', text: '#92400E' },
      SYSTEM: { bg: '#FEE2E2', text: '#991B1B' },
    };
    return colors[source] || colors.USER_UPLOAD;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div style={{ padding: '2rem' }}>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <ol style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          listStyle: 'none', 
          padding: 0,
          fontSize: '0.875rem',
          color: 'var(--gray-600)'
        }}>
          <li><a href="/" style={{ color: 'var(--primary-600)', textDecoration: 'none' }}>Dashboard</a></li>
          <li>›</li>
          {projectId ? (
            <>
              <li><a href="/companies" style={{ color: 'var(--primary-600)', textDecoration: 'none' }}>Projects</a></li>
              <li>›</li>
              <li style={{ color: 'var(--gray-900)', fontWeight: '500' }}>Documents</li>
            </>
          ) : (
            <li style={{ color: 'var(--gray-900)', fontWeight: '500' }}>Documents</li>
          )}
        </ol>
      </nav>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '2rem', color: 'var(--gray-900)' }}>
            📚 Document Library
          </h1>
          <p style={{ margin: '0.5rem 0 0 0', color: 'var(--gray-600)' }}>
            Manage files and AI-generated content
          </p>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--primary-600)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ☁️ Upload Document
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#FEE2E2',
          border: '1px solid #FCA5A5',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#991B1B' }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#991B1B' }}
          >
            ×
          </button>
        </div>
      )}
      {success && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#D1FAE5',
          border: '1px solid #6EE7B7',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#065F46' }}>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#065F46' }}
          >
            ×
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid var(--gray-200)',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <input
            type="text"
            placeholder="🔍 Search documents..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
          <select
            value={filters.document_type}
            onChange={(e) => setFilters({ ...filters, document_type: e.target.value })}
            style={{
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          >
            <option value="">All Types</option>
            <option value="MARKDOWN">Markdown</option>
            <option value="PDF">PDF</option>
            <option value="DOCX">Word</option>
            <option value="IMAGE">Image</option>
            <option value="SPREADSHEET">Spreadsheet</option>
            <option value="PRESENTATION">Presentation</option>
            <option value="VIDEO">Video</option>
          </select>
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            style={{
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          >
            <option value="">All Categories</option>
            <option value="strategy">Strategy</option>
            <option value="research">Research</option>
            <option value="analysis">Analysis</option>
            <option value="presentation">Presentation</option>
            <option value="report">Report</option>
          </select>
          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            style={{
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          >
            <option value="">All Sources</option>
            <option value="USER_UPLOAD">User Upload</option>
            <option value="AI_GENERATED">AI Generated</option>
            <option value="AI_ACTION">AI Action</option>
            <option value="WORKFLOW">Workflow</option>
          </select>
        </div>
      </div>

      {/* Documents Grid */}
      {loading && !documents.length ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner"></div>
        </div>
      ) : documents.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem',
          backgroundColor: 'var(--gray-50)',
          border: '2px dashed var(--gray-300)',
          borderRadius: '0.5rem'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-700)' }}>No documents found</h3>
          <p style={{ margin: 0, color: 'var(--gray-500)' }}>
            Upload your first document to get started
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
          {documents.map((doc) => {
            const sourceColor = getSourceColor(doc.source);
            return (
              <div
                key={doc.id}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '0.5rem',
                  padding: '1.5rem',
                  opacity: doc.is_archived ? 0.6 : 1,
                  transition: 'box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '2rem', marginRight: '1rem' }}>
                    {getIcon(doc.document_type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{
                      margin: '0 0 0.25rem 0',
                      fontSize: '1.125rem',
                      color: 'var(--gray-900)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={doc.title}>
                      {doc.title}
                    </h3>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      {formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}
                    </div>
                  </div>
                </div>

                {doc.description && (
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--gray-600)',
                    marginBottom: '1rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4'
                  }}>
                    {doc.description}
                  </p>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: sourceColor.bg,
                    color: sourceColor.text,
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    marginRight: '0.5rem'
                  }}>
                    {doc.source?.replace('_', ' ')}
                  </span>
                  {doc.category && (
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      backgroundColor: 'var(--gray-100)',
                      color: 'var(--gray-700)',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}>
                      {doc.category}
                    </span>
                  )}
                </div>

                {doc.tags && doc.tags.length > 0 && (
                  <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {doc.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} style={{
                        padding: '0.125rem 0.5rem',
                        backgroundColor: 'var(--gray-100)',
                        color: 'var(--gray-600)',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem'
                      }}>
                        {tag}
                      </span>
                    ))}
                    {doc.tags.length > 3 && (
                      <span style={{
                        padding: '0.125rem 0.5rem',
                        backgroundColor: 'var(--gray-100)',
                        color: 'var(--gray-600)',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem'
                      }}>
                        +{doc.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleView(doc)}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: 'white',
                      color: 'var(--primary-600)',
                      border: '1px solid var(--primary-600)',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                    title="View"
                  >
                    👁️ View
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: 'var(--primary-600)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                    title="Download"
                  >
                    ⬇️ Download
                  </button>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'white',
                      color: '#DC2626',
                      border: '1px solid #DC2626',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {uploadOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: 'var(--gray-900)' }}>
              Upload Document
            </h2>

            <label
              htmlFor="file-upload"
              style={{
                display: 'block',
                padding: '2rem',
                backgroundColor: 'var(--gray-50)',
                border: '2px dashed var(--gray-300)',
                borderRadius: '0.5rem',
                textAlign: 'center',
                cursor: 'pointer',
                marginBottom: '1.5rem'
              }}
            >
              {selectedFile ? (
                <div>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📎</div>
                  <div style={{ fontWeight: '500', color: 'var(--gray-900)' }}>{selectedFile.name}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                    Click to change file
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>☁️</div>
                  <div style={{ fontWeight: '500', color: 'var(--gray-700)' }}>Click to select file</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                    or drag and drop
                  </div>
                </div>
              )}
              <input
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </label>

            <input
              type="text"
              placeholder="Title"
              value={uploadMetadata.title}
              onChange={(e) => setUploadMetadata({ ...uploadMetadata, title: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            />

            <textarea
              placeholder="Description (optional)"
              value={uploadMetadata.description}
              onChange={(e) => setUploadMetadata({ ...uploadMetadata, description: e.target.value })}
              rows="3"
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />

            <input
              type="text"
              placeholder="Category (e.g., strategy, research)"
              value={uploadMetadata.category}
              onChange={(e) => setUploadMetadata({ ...uploadMetadata, category: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            />

            <input
              type="text"
              placeholder="Tags (comma-separated)"
              value={uploadMetadata.tags}
              onChange={(e) => setUploadMetadata({ ...uploadMetadata, tags: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1.5rem',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            />

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setUploadOpen(false);
                  setSelectedFile(null);
                  setUploadMetadata({ title: '', description: '', category: '', tags: '' });
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'white',
                  color: 'var(--gray-700)',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={loading || !selectedFile}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: selectedFile && !loading ? 'var(--primary-600)' : 'var(--gray-400)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: selectedFile && !loading ? 'pointer' : 'not-allowed',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewOpen && selectedDocument && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '2rem', marginRight: '1rem' }}>
                {getIcon(selectedDocument.document_type)}
              </div>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--gray-900)' }}>
                {selectedDocument.title}
              </h2>
            </div>

            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '0.5rem' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                <strong>File:</strong> {selectedDocument.file_name} ({formatFileSize(selectedDocument.file_size)})
              </p>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                <strong>Type:</strong> {selectedDocument.document_type} • <strong>Source:</strong> {selectedDocument.source}
              </p>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                <strong>Created:</strong> {formatDate(selectedDocument.created_at)}
              </p>
            </div>

            {selectedDocument.description && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>Description:</strong>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                  {selectedDocument.description}
                </p>
              </div>
            )}

            {selectedDocument.content && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>Content:</strong>
                <pre style={{
                  margin: '0.5rem 0 0 0',
                  padding: '1rem',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  maxHeight: '300px',
                  overflow: 'auto',
                  fontFamily: 'monospace'
                }}>
                  {selectedDocument.content}
                </pre>
              </div>
            )}

            {selectedDocument.tags && selectedDocument.tags.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <strong style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Tags:
                </strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {selectedDocument.tags.map((tag, idx) => (
                    <span key={idx} style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: 'var(--gray-100)',
                      color: 'var(--gray-700)',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem'
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setViewOpen(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'white',
                  color: 'var(--gray-700)',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
              <button
                onClick={() => handleDownload(selectedDocument)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--primary-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                ⬇️ Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentLibrary;
